import { useState } from "react";
import { useEmbed } from "@/contexts/EmbedContext";
import { useAutoResize } from "@/hooks/use-auto-resize";
import { cn } from "@/lib/utils";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { CurrencySwitcher } from "./CurrencySwitcher";
import { GeoLanguageBanner } from "./GeoLanguageBanner";
import { ThemeToggle } from "./ThemeToggle";
import { CartDrawer } from "./CartDrawer";
import { SiteFooter } from "./SiteFooter";
import { LocalizedLink } from "@/components/LocalizedLink";
import { useLocalizedUrl } from "@/hooks/use-localized-routes";
import logoMain from "@/assets/guestly-logo-main.png";
import { useIsMobile } from "@/hooks/use-mobile";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Menu, LogOut, LogIn, ChevronDown } from "lucide-react";
import { IconAvatar, IconHeart } from "@/components/icons";
import { useUiTranslations } from "@/hooks/use-ui-translations";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";

export function PublicLayout({ children, fullBleed = false }: { children: React.ReactNode; fullBleed?: boolean }) {
  const { isEmbed } = useEmbed();
  const isMobile = useIsMobile();
  const [menuOpen, setMenuOpen] = useState(false);
  const { t } = useUiTranslations();
  const { user, role, signOut, loading: authLoading } = useAuth();
  const { localizeUrl } = useLocalizedUrl();
  useAutoResize();

  const navLinks = [
    { href: "/experiences", label: t("nav.experiences") },
    { href: "/destinations", label: t("nav.destinations") },
    { href: "/partner", label: t("nav.partner"), children: [
      { href: "/partner", label: t("nav.partner") },
      { href: "/affiliates", label: t("nav.affiliateProgram") },
    ]},
    { href: "/about", label: t("nav.about") },
  ];

  const UserMenu = () => {
    if (authLoading) return null;

    if (!user) {
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="rounded-full">
              <IconAvatar className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem asChild>
              <a href="/login" className="flex items-center gap-2">
                <LogIn className="h-4 w-4" />{t("nav.signIn")}
              </a>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">{t("nav.preferences") || "Preferences"}</DropdownMenuLabel>
            <div className="px-2 py-1.5 flex items-center gap-2">
              <LanguageSwitcher />
            </div>
            <div className="px-2 py-1.5 flex items-center gap-2">
              <CurrencySwitcher />
            </div>
            <div className="px-2 py-1.5 flex items-center gap-2">
              <ThemeToggle />
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="rounded-full">
            <IconAvatar className="h-5 w-5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem asChild>
            <a href="/account" className="flex items-center gap-2"><IconAvatar className="h-4 w-4" />{t("nav.myAccount")}</a>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <a href="/account/favorites" className="flex items-center gap-2"><IconHeart className="h-4 w-4" />{t("nav.favorites")}</a>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">{t("nav.preferences") || "Preferences"}</DropdownMenuLabel>
          <div className="px-2 py-1.5 flex items-center gap-2">
            <LanguageSwitcher />
          </div>
          <div className="px-2 py-1.5 flex items-center gap-2">
            <CurrencySwitcher />
          </div>
          <div className="px-2 py-1.5 flex items-center gap-2">
            <ThemeToggle />
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => signOut()} className="flex items-center gap-2">
            <LogOut className="h-4 w-4" />{t("nav.signOut")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <div className={cn("min-h-screen flex flex-col overflow-x-hidden", isEmbed ? "bg-transparent" : "bg-background")}>
      {!isEmbed && (
        <>
          <header className="border-b border-border bg-card">
            <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4">
              <a href={localizeUrl("/experiences")} className="flex items-center">
                <img src={logoMain} alt="Guestly Experiences" className="h-8 w-auto" width={120} height={32} />
              </a>

              {/* Desktop nav */}
              <nav className="hidden md:flex items-center gap-6">
                {navLinks.map((link) =>
                  link.children ? (
                    <DropdownMenu key={link.href}>
                      <DropdownMenuTrigger className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 outline-none">
                        {link.label} <ChevronDown className="h-3 w-3" />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start">
                        {link.children.map((child) => (
                          <DropdownMenuItem key={child.href} asChild>
                            <a href={localizeUrl(child.href)} className="cursor-pointer">{child.label}</a>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : (
                    <a
                      key={link.href}
                      href={localizeUrl(link.href)}
                      className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {link.label}
                    </a>
                  )
                )}
                <CartDrawer />
                <UserMenu />
              </nav>

              {/* Mobile hamburger */}
              {isMobile && (
                <div className="flex items-center gap-1 md:hidden">
                  <CartDrawer />
                  <UserMenu />
                  <button
                    onClick={() => setMenuOpen(true)}
                    className="flex items-center justify-center min-w-[48px] min-h-[48px]"
                    aria-label="Open menu"
                  >
                    <Menu className="h-6 w-6 text-foreground" />
                  </button>
                </div>
              )}
            </div>
          </header>
          <GeoLanguageBanner />
        </>
      )}

      {/* Mobile menu sheet */}
      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="right" className="w-[280px]">
          <SheetHeader>
            <SheetTitle>{t("nav.menu")}</SheetTitle>
          </SheetHeader>
          <nav className="mt-6 flex flex-col gap-2">
            {navLinks.flatMap((link) =>
              link.children
                ? link.children.map((child) => (
                    <a
                      key={child.href}
                      href={localizeUrl(child.href)}
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center min-h-[48px] px-3 rounded-md text-base font-medium text-foreground hover:bg-accent transition-colors"
                    >
                      {child.label}
                    </a>
                  ))
                : [(
                    <a
                      key={link.href}
                      href={localizeUrl(link.href)}
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center min-h-[48px] px-3 rounded-md text-base font-medium text-foreground hover:bg-accent transition-colors"
                    >
                      {link.label}
                    </a>
                  )]
            )}
          </nav>
        </SheetContent>
      </Sheet>

      <main className={cn(
        "flex-1 max-w-full",
        fullBleed ? "" : "mx-auto max-w-6xl px-4",
        isEmbed ? "py-2" : fullBleed ? "" : "py-8"
      )}>
        {children}
      </main>
      <SiteFooter />
    </div>
  );
}
